import { Modal } from 'react-bootstrap';
import { Search, GitCompare } from "lucide-react";
import Skeleton from 'react-loading-skeleton';
import 'react-loading-skeleton/dist/skeleton.css';
import React, { useState, useEffect, useRef, useMemo } from "react";
import { Nav, NavItem, NavLink } from "reactstrap";
import classnames from "classnames";
import PlatformIcon from "../PlatformIcon";
import { usePlatforms } from '../../../contexts/PlatformsContext';
import { useKeywordSearchSWR } from '../../../hooks/useKeywordSearchSWR'
import { getLocalItem } from '../../../utils/storage'
import { api } from '../../../utils/variables'
import axios from 'axios'
import FilterPanel from '../../KeywordTrackingNewUIPage/FilterPanel'
import { useFilters } from '../../../hooks/useFilters'
import ConfirmRemoveTagModal from '../../Common/Modal/ConfirmRemoveTagModal'
import { useSearchProgress } from '../../../hooks/useSearchProgress';
import SearchProgressModal from '../Modal/SearchProgressModal';

const KeywordSuggestionsDetailsModal = ({
    show,
    handleClose,
    title,
    searchTerm,
    onSearchChange,
    onSearchClick,
    results = [],
    loading = false,
    onSelectApp, // optional callback on "Select"
    onUpgrade,
    trackedIds = [],
    enableFallbackPolling = true,
    pollMaxAttempts = 5,
    pollInitialIntervalMs = 5000,
    pollDebug = false,
    createTag = null,
    onRemoveFromTag,
    tags = [],
    onOpenGroup,
    onBulkAddToTag,
    onAddToTag,
    initialPlatform = null,
    // Custom label for the selection button
    selectButtonLabel = "View Keywords",
    // Comparison mode props
    comparisonMode = false,
    onCompareApps = null,
    comparisonApps = [],
}) => {
    const { getActivePlatforms, getPlatformDisplayName, loading: platformsLoading } = usePlatforms();
    const [modalLoading, setModalLoading] = useState(false)

    // Default to first active platform from API
    const [activeTab, setActiveTab] = useState(() => {
        const activePlatforms = getActivePlatforms();
        return activePlatforms.length > 0 ? activePlatforms[0].id : "meta";
    });
    const [showTrackedOnly, setShowTrackedOnly] = useState(false)
    const [localInput, setLocalInput] = useState(searchTerm || "")
    const [submittedKeyword, setSubmittedKeyword] = useState(null)
    const [pollAttempt, setPollAttempt] = useState(0)
    const { filterSections } = useFilters()
    const [showFilters, setShowFilters] = useState(false)
    const [modalFilters, setModalFilters] = useState({})
    const [modalFiltersRaw, setModalFiltersRaw] = useState({})

    const swr = useKeywordSearchSWR({
        keyword: submittedKeyword,
        page: 1,
        platform: initialPlatform ? initialPlatform : (activeTab !== "all" ? activeTab : ""),
        filters: modalFilters,
        intervalMs: enableFallbackPolling ? 0 : pollInitialIntervalMs,
    })
    const swrData = swr?.data
    const hasParentResults = Array.isArray(results) && results.length > 0
    const effectiveResults = (swrData && swrData.games) ? swrData.games : results

    let swrIsLoading = false
    if (!!submittedKeyword) {
        if (!swrData) {
            swrIsLoading = !hasParentResults
        } else if (swrData?.action === "LOADING") {
            swrIsLoading = true
        } else if (swrData?.action === "DISPLAY") {
            const hasAnyGames = (Array.isArray(swrData.games) && swrData.games.length > 0) || hasParentResults
            const isStillPolling = pollAttempt > 0 && pollAttempt < 3 && !hasAnyGames
            swrIsLoading = !!(swr.isLoading || (swr.isValidating && !hasAnyGames) || isStillPolling)
        } else {
            const hasAnyGames = (Array.isArray(swrData.games) && swrData.games.length > 0) || hasParentResults
            const isStillPolling = pollAttempt < 3 && !hasAnyGames
            swrIsLoading = !!(swr.isLoading || swr.isValidating || isStillPolling)
        }
    }

    const isLoadingResults = loading || swrIsLoading

    useEffect(() => {
        if (!swrData) return
        const status = swrData?.status
        const action = swrData?.action || ""
        if (status === 400 && action === "UPGRADE") {
            setSubmittedKeyword(null)
            try {
                if (typeof onUpgrade === "function") onUpgrade()
            } catch (err) { }
        }
    }, [swrData, onUpgrade])

    const pollTimerRef = useRef(null)
    const pollAttemptRef = useRef(0)
    const currentIntervalRef = useRef(pollInitialIntervalMs)

    const clearPoll = () => {
        if (pollTimerRef.current) {
            clearTimeout(pollTimerRef.current)
            pollTimerRef.current = null
        }
    }

    useEffect(() => {
        clearPoll()
        pollAttemptRef.current = 0
        setPollAttempt(0)
        currentIntervalRef.current = pollInitialIntervalMs
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [submittedKeyword, modalFilters, activeTab])

    useEffect(() => {
        if (!submittedKeyword || !enableFallbackPolling) return
        clearPoll()
        pollAttemptRef.current = 0
        setPollAttempt(0)
        currentIntervalRef.current = pollInitialIntervalMs

        const schedulePoll = () => {
            if (pollAttemptRef.current >= pollMaxAttempts) return
            pollAttemptRef.current += 1
            setPollAttempt(pollAttemptRef.current)
            pollTimerRef.current = setTimeout(() => {
                try { swr.mutate() } catch (err) { }
                const isStillLoading = swrData?.action === "LOADING"
                const noResultsYet = !swrData || !Array.isArray(swrData.games) || swrData.games.length === 0
                if (isStillLoading || (noResultsYet && pollAttemptRef.current < 3)) {
                    currentIntervalRef.current = Math.min(currentIntervalRef.current * 2, 60000)
                    schedulePoll()
                }
            }, currentIntervalRef.current)
        }

        try { swr.mutate() } catch (err) { }
        const isStillLoading = swrData?.action === "LOADING"
        const noResultsYet = !swrData || !Array.isArray(swrData.games) || swrData.games.length === 0
        if (isStillLoading || noResultsYet) schedulePoll()

        return () => {
            if (pollTimerRef.current) {
                clearTimeout(pollTimerRef.current)
                pollTimerRef.current = null
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [submittedKeyword, swrData?.action, enableFallbackPolling, pollMaxAttempts, pollInitialIntervalMs, pollDebug, swrData?.games?.length])

    const filteredResults = useMemo(() => {
        if (!submittedKeyword && !swrData) {
            if (localInput && String(localInput).trim() && searchTerm && String(searchTerm).trim() && Array.isArray(results) && results.length > 0) {
                const localStored = getLocalItem('comparisonBetween') || []
                const localTrackedIds = Array.isArray(localStored) ? localStored.map(g => g?.game_details?.item_id).filter(Boolean) : []
                const effectiveTrackedIds = trackedIds && trackedIds.length > 0 ? trackedIds : localTrackedIds
                const isTracked = game => {
                    const id = game?.item_id || game?._id || game?.game_details?.item_id || game?.game_details?._id || null
                    return effectiveTrackedIds && effectiveTrackedIds.length > 0 && id != null && effectiveTrackedIds.includes(id)
                }
                let filtered = results.slice()
                if (showTrackedOnly) filtered = filtered.filter(isTracked)
                else filtered = filtered.filter(game => game.platform?.toLowerCase() === activeTab.toLowerCase())
                return filtered
            }
            return []
        }
        const baseResults = effectiveResults || []
        if (baseResults && baseResults.length > 0) {
            const localStored = getLocalItem('comparisonBetween') || []
            const localTrackedIds = Array.isArray(localStored) ? localStored.map(g => g?.game_details?.item_id).filter(Boolean) : []
            const effectiveTrackedIds = trackedIds && trackedIds.length > 0 ? trackedIds : localTrackedIds
            const isTracked = game => {
                const id = game?.item_id || game?._id || game?.game_details?.item_id || game?.game_details?._id || null
                return effectiveTrackedIds && effectiveTrackedIds.length > 0 && id != null && effectiveTrackedIds.includes(id)
            }
            let filtered = baseResults.slice()
            if (showTrackedOnly) {
                filtered = filtered.filter(isTracked)
            } else {
                filtered = filtered.filter(game => game.platform?.toLowerCase() === activeTab.toLowerCase())
                if (submittedKeyword && submittedKeyword.trim()) {
                    filtered = filtered.slice().sort((a, b) => (isTracked(a) ? 0 : 1) - (isTracked(b) ? 0 : 1))
                } else {
                    filtered = filtered.slice().sort((a, b) => (isTracked(a) ? 1 : 0) - (isTracked(b) ? 1 : 0))
                }
            }
            return filtered
        }
        return []
    }, [results, activeTab, swrData, trackedIds, submittedKeyword, showTrackedOnly, searchTerm, localInput, effectiveResults])

    useEffect(() => {
        if (!show) {
            setLocalInput("")
            setSubmittedKeyword(null)
            setShowFilters(false)
            setModalFilters({})
            setModalFiltersRaw({})
            setSelectedIds([])
            setBulkTagSelection("")
            setSelectedMemberTags({})
            setConfirmRemove(null)
            setRowLoadingMap({})
            setModalLoading(false)

            if (onSearchChange) {
                try { onSearchChange({ target: { value: "" } }) } catch (err) { }
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [show])

    useEffect(() => { setLocalInput(searchTerm || "") }, [searchTerm])

    useEffect(() => {
        if (!localInput || !String(localInput).trim()) setSubmittedKeyword(null)
    }, [localInput])

    useEffect(() => {
        if (show && searchTerm && String(searchTerm).trim() && !submittedKeyword) setSubmittedKeyword(String(searchTerm).trim())
    }, [show, searchTerm, submittedKeyword])

    useEffect(() => { 
        if (show) {
            const activePlatforms = getActivePlatforms();
            if (activePlatforms.length > 0) {
                setActiveTab(activePlatforms[0].id);
            }
        }
    }, [show, getActivePlatforms])

    const handleTrackClick = async (game, addToTagAfter = null, rowId = null) => {
        // Call the callback to pass selected app to parent
        if (onSelectApp) {
            onSelectApp(game)
        }
        // Modal will be closed by parent component
    }

    const handleTabChange = tab => setActiveTab(tab)

    const effectiveTrackedIdsForRender = trackedIds || []
    const inCreateMode = !!createTag

    const [selectedIds, setSelectedIds] = useState([])
    const [bulkTagSelection, setBulkTagSelection] = useState("")
    const [selectedMemberTags, setSelectedMemberTags] = useState({})
    const [confirmRemove, setConfirmRemove] = useState(null)
    const [rowLoadingMap, setRowLoadingMap] = useState({})

    // Comparison mode state
    const [compareMode, setCompareMode] = useState(comparisonMode || false)
    const [compareSelectedIds, setCompareSelectedIds] = useState([])

    // Search progress modal state
    const { progress, showProgressModal, cancel: cancelSearchProgress } = useSearchProgress({
        loading: isLoadingResults,
    });

    const setRowLoading = (id, value) => setRowLoadingMap(prev => ({ ...(prev || {}), [id]: !!value }))

    const handleBulkAdd = async () => {
        if (!onBulkAddToTag || selectedIds.length === 0 || !bulkTagSelection) return
        try {
            setModalLoading(true)
            await onBulkAddToTag(selectedIds, bulkTagSelection)
            setSelectedIds([])
            setBulkTagSelection("")
        } catch (err) { } finally { setModalLoading(false) }
    }

    // Comparison mode functions
    const toggleCompareMode = () => {
        setCompareMode(!compareMode)
        setCompareSelectedIds([])
    }

    const toggleCompareSelection = (appId) => {
        setCompareSelectedIds(prev =>
            prev.includes(appId)
                ? prev.filter(id => id !== appId)
                : [...prev, appId]
        )
    }

    const handleCompareSelected = async () => {
        if (!onCompareApps || compareSelectedIds.length === 0) return

        setModalLoading(true)
        try {
            const selectedApps = filteredResults.filter(game =>
                compareSelectedIds.includes(game.item_id || game._id)
            )
            await onCompareApps(selectedApps)
            setCompareSelectedIds([])
            setCompareMode(false)
            handleClose() // Close search modal
        } catch (err) { } finally { setModalLoading(false) }
    }

    return (
        <>
            <SearchProgressModal
                show={showProgressModal}
                progress={progress}
                title="Searching for apps"
                onCancel={() => { cancelSearchProgress(); handleClose(); }}
                hasResults={filteredResults.length > 0}
            />
            <Modal show={show} onHide={handleClose} centered size="lg" className="modal_key">
                <Modal.Header closeButton>
                    <Modal.Title style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                        {title || "Select an App"}
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body style={{ minHeight: '400px' }}>
                    {modalLoading && (
                        <div style={{ position: "fixed", top: 0, left: 0, width: "100vw", height: "100vh", backgroundColor: "rgba(0,0,0,0.5)", display: "flex", justifyContent: "center", alignItems: "center", zIndex: 1050 }}>
                            <div className="spinner-border text-light" role="status" aria-hidden="true" />
                        </div>
                    )}
                    {showFilters && (
                        <FilterPanel
                            open={showFilters}
                            sections={filterSections.length > 0 ? filterSections : undefined}
                            initialValues={modalFiltersRaw}
                            onClose={() => setShowFilters(false)}
                            onApply={sel => {
                                const apiFilters = {}
                                if (sel) {
                                    if (sel.price) apiFilters.price = sel.price
                                    if (sel.rating) apiFilters.rating = sel.rating
                                    if (sel.downloads) apiFilters.downloads = sel.downloads
                                    if (sel.revenue) apiFilters.revenue = sel.revenue
                                    if (sel.release_date) apiFilters.release_date = sel.release_date
                                    if (sel.genre) apiFilters.genre = sel.genre
                                    if (sel.category) apiFilters.category = sel.category;
                                    if (sel.bundle) apiFilters.bundle = sel.bundle
                                    if (sel.number_of_players) apiFilters.number_of_players = sel.number_of_players
                                    if (sel.platform) {
                                        apiFilters.platform = sel.platform
                                        const selectedPlatform = Array.isArray(sel.platform) ? sel.platform[0] : sel.platform
                                        if (selectedPlatform) {
                                            setActiveTab(selectedPlatform)
                                        }
                                    }
                                    if (sel.sort_by) apiFilters.sort_by = sel.sort_by
                                    else if (sel.sort) apiFilters.sort_by = sel.sort
                                }
                                setModalFilters(apiFilters)
                                setModalFiltersRaw(sel || {})
                                if (localInput && localInput.trim()) setSubmittedKeyword(localInput.trim())
                                setShowFilters(false)
                             }}
                        />
                    )}
                    <div className="key_track_search" style={{ maxWidth: "100%" }}>
                        <div className="key_track_search_inn">
                            <i className="fa-solid fa-magnifying-glass" />
                            <input type="text" placeholder="Enter App Name" value={localInput} onChange={e => setLocalInput(e.target.value)} onKeyDown={e => {
                                if (e.key === 'Enter') {
                                    e.preventDefault()
                                    const term = localInput.trim()
                                    if (term) {
                                        if (typeof onSearchChange === 'function') onSearchChange({ target: { value: term } })
                                        setSubmittedKeyword(term)
                                        if (typeof onSearchClick === 'function') onSearchClick()
                                    }
                                }
                            }} />
                            <button onClick={() => {
                                const term = localInput.trim()
                                if (typeof onSearchChange === 'function') onSearchChange({ target: { value: term } })
                                setSubmittedKeyword(term)
                                if (typeof onSearchClick === 'function') onSearchClick()
                                swr.mutate()
                            }} disabled={modalLoading || isLoadingResults}>
                                {isLoadingResults && <span className="spinner-border spinner-border-sm" style={{ marginRight: 8 }} role="status" aria-hidden="true" />}
                                Search
                            </button>
                            <button type="button" className="filter-trigger" onClick={() => setShowFilters(true)} style={{ marginLeft: 8 }}>
                                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" style={{ marginRight: 8 }} xmlns="http://www.w3.org/2000/svg">
                                    <path d="M10 18h4v-2h-4v2zM4 6h16v2H4V6zm3 6h10v-2H7v2z" fill="#fff" />
                                </svg>
                                Filter
                                {modalFiltersRaw && Object.keys(modalFiltersRaw).length > 0 && (
                                    <span className="filter-badge">
                                        {Object.values(modalFiltersRaw).reduce((acc, v) => acc + (v == null ? 0 : Array.isArray(v) ? v.length : String(v).trim() ? 1 : 0), 0)}
                                    </span>
                                )}
                            </button>
                        </div>
                    </div>

                    {/* Platform Tabs */}
                    <div
                        className="platform-tabs"
                        style={{ marginTop: "20px", marginBottom: "15px", padding: '0 12px' }}
                    >
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                            <Nav pills style={{ marginLeft: 0, gap: '8px' }}>
                                {!platformsLoading && getActivePlatforms().map((platform) => (
                                    <NavItem key={platform.id}>
                                        <NavLink
                                            style={{
                                                cursor: "pointer",
                                                backgroundColor:
                                                    activeTab === platform.id ? "#f6255a" : "transparent",
                                                color: activeTab === platform.id ? "#fff" : "inherit",
                                                borderColor:
                                                    activeTab === platform.id ? "#f6255a" : "transparent",
                                            }}
                                            className={classnames({
                                                active: activeTab === platform.id,
                                            })}
                                            onClick={() => handleTabChange(platform.id)}
                                        >
                                            <span className="d-block d-sm-block">
                                                <PlatformIcon platform={platform.id} style={{ height: "16px", marginRight: "5px" }} />
                                                {platform.display_name}
                                            </span>
                                        </NavLink>
                                    </NavItem>
                                ))}
                                {platformsLoading && (
                                    <NavItem>
                                        <NavLink style={{ cursor: "default", color: "#999" }}>
                                            <span className="d-block d-sm-block">Loading...</span>
                                        </NavLink>
                                    </NavItem>
                                )}
                            </Nav>
                            <div style={{ marginLeft: 12, color: '#fff', display: 'flex', alignItems: 'center', gap: 8 }}>
                                <label style={{ display: 'inline-flex', alignItems: 'center', gap: 8, cursor: 'pointer', margin: 0 }}>
                                    <input
                                        type="checkbox"
                                        checked={showTrackedOnly}
                                        onChange={e => setShowTrackedOnly(!!e.target.checked)}
                                        style={{ width: 16, height: 16, accentColor: '#F6255A', margin: 0 }}
                                    />
                                    <span style={{ fontSize: 13, userSelect: 'none', fontWeight: 500 }}>Tracked Apps</span>
                                </label>
                            </div>
                        </div>
                    </div>

                    {/* Compare Mode Toggle — only relevant to pages that pass onCompareApps (e.g. Keyword Suggestions) */}
                    {onCompareApps && (
                    <div className="d-flex align-items-center justify-content-between mb-3" style={{ padding: '0 12px' }}>
                        <div className="d-flex align-items-center gap-2">
                            <input
                                type="checkbox"
                                id="compareMode"
                                checked={compareMode}
                                onChange={toggleCompareMode}
                                style={{
                                    width: '18px',
                                    height: '18px',
                                    cursor: 'pointer',
                                    accentColor: '#F6255A'
                                }}
                            />
                            <label htmlFor="compareMode" style={{
                                color: '#fff',
                                fontSize: '14px',
                                fontWeight: 500,
                                cursor: 'pointer',
                                margin: 0,
                                display: 'flex',
                                alignItems: 'center',
                                gap: '6px'
                            }}>
                                <GitCompare size={16} color="#F6255A" />
                                Compare Mode
                            </label>
                        </div>
                        {compareMode && compareSelectedIds.length > 0 && (
                            <button
                                className="btn btn-sm"
                                onClick={handleCompareSelected}
                                disabled={modalLoading}
                                style={{
                                    background: compareSelectedIds.length > 0 ? '#F6255A' : '#2A2C38',
                                    border: 'none',
                                    color: '#fff',
                                    padding: '6px 12px',
                                    borderRadius: '6px',
                                    fontSize: '12px',
                                    fontWeight: 600,
                                    boxShadow: compareSelectedIds.length > 0 ? '0 4px 12px rgba(246, 37, 90, 0.3)' : 'none',
                                    transition: 'all 0.2s ease'
                                }}
                            >
                                {modalLoading ? (
                                    <span className="spinner-border spinner-border-sm" style={{ marginRight: 4 }} />
                                ) : null}
                                Compare Selected ({compareSelectedIds.length})
                            </button>
                        )}
                    </div>
                    )}

                    <div className="grp_result_btm key_detail_modal">
                        <h6>Search Result ({getPlatformDisplayName(activeTab)})</h6>
                        <div className="app_modal_scroll">
                            {(isLoadingResults && (submittedKeyword || (searchTerm && String(searchTerm).trim()))) ? (
                                Array.from({ length: 3 }).map((_, index) => (
                                    <div key={index} className="grp_result_btm_box">
                                        <div className="grp_result_btm_box_lt">
                                            <div className="grp_result_btm_box_lt_img">
                                                <Skeleton width={96} height={54} borderRadius={8} baseColor="#2a2a35" highlightColor="#2a2a35" />
                                            </div>
                                            <div className="app_detail_txt">
                                                <h4><Skeleton width={120} baseColor="#2a2a35" highlightColor="#2a2a35" /></h4>
                                                <p><Skeleton width={80} baseColor="#2a2a35" highlightColor="#2a2a35" /></p>
                                            </div>
                                        </div>
                                        <div className="grp_result_btm_box_rt">
                                            <Skeleton width={60} height={24} baseColor="#2a2a35" highlightColor="#2a2a35" />
                                        </div>
                                    </div>
                                ))
                            ) : filteredResults.length === 0 ? (
                                <div className="text-center my-2" style={{ background: "rgba(30, 31, 41, 0.4)", borderRadius: "16px", border: "1px solid rgba(255, 255, 255, 0.05)", padding: "48px 20px", marginTop: "12px" }}>
                                    <div style={{ width: "56px", height: "56px", background: "rgba(246, 37, 90, 0.1)", borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 16px" }}>
                                        <Search size={24} color="#F6255A" />
                                    </div>
                                    <h6 style={{ color: "#fff", fontWeight: 600, marginBottom: "8px", fontSize: "16px" }}>{submittedKeyword ? "No Results Found" : "Search Apps & Games"}</h6>
                                    <p style={{ color: "rgba(255, 255, 255, 0.5)", fontSize: "13px", maxWidth: "280px", margin: "0 auto", lineHeight: "1.5" }}>
                                        {submittedKeyword ? (showTrackedOnly ? `We couldn't find any tracked apps matching "${submittedKeyword}"` : `We couldn't find any ${activeTab} results for "${submittedKeyword}"`) : "Type a name to search and track your favorite apps across platforms."}
                                    </p>
                                </div>
                            ) : (
                                filteredResults.map((game, index) => {
                                    const id = game.item_id || game._id
                                    const alreadyTracked = effectiveTrackedIdsForRender && effectiveTrackedIdsForRender.includes(id)
                                    const isSelectedForCompare = compareSelectedIds.includes(id)

                                    return (
                                        <div key={id || index} className="grp_result_btm_box">
                                            <div className="grp_result_btm_box_lt" style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                                                {compareMode && (
                                                    <input
                                                        type="checkbox"
                                                        checked={isSelectedForCompare}
                                                        onChange={() => toggleCompareSelection(id)}
                                                        style={{
                                                            width: '18px',
                                                            height: '18px',
                                                            cursor: 'pointer',
                                                            accentColor: '#F6255A',
                                                            marginLeft: '8px'
                                                        }}
                                                    />
                                                )}
                                                <div className="grp_result_btm_box_lt_img">
                                                    <img className="modal-app-img" src={game.image ?? game.image_url} alt={game.name} />
                                                </div>
                                                <div className="app_detail_txt">
                                                    <h4>{game.name}</h4>
                                                    <p>
                                                        <PlatformIcon platform={game.platform} style={{ height: "16px", marginRight: "5px" }} />
                                                        {getPlatformDisplayName(game.platform)}
                                                    </p>
                                                    {alreadyTracked && (
                                                        <div style={{ marginTop: 4 }}>
                                                            <span style={{ background: 'rgba(246,37,90,0.12)', color: '#fff', borderRadius: 6, fontSize: 12, fontWeight: 600, padding: '6px 10px', display: 'inline-block' }}>Already Tracked</span>
                                                        </div>
                                                    )}
                                                </div>
                                            </div>
                                            <div className="grp_result_btm_box_rt">
                                                {!compareMode && (
                                                    <a
                                                        href="#"
                                                        onClick={async e => {
                                                            e.preventDefault()
                                                            handleTrackClick(game, null, id)
                                                        }}
                                                        style={{
                                                            pointerEvents: modalLoading ? "none" : "auto",
                                                            opacity: modalLoading ? 0.6 : 1,
                                                        }}
                                                    >
                                                        {selectButtonLabel}
                                                    </a>
                                                )}
                                            </div>
                                        </div>
                                    )
                                })
                            )}
                        </div>


                    </div>
                    {
                        confirmRemove && (
                            <ConfirmRemoveTagModal
                                show={true}
                                onClose={() => setConfirmRemove(null)}
                                onConfirm={async ({ removeFromEverywhere }) => {
                                    if (!confirmRemove) return
                                    const { itemId, tagName } = confirmRemove
                                    setModalLoading(true)
                                    try {
                                        const removeGlobal = removeFromEverywhere || !tagName
                                        if (typeof onRemoveFromTag === 'function') await onRemoveFromTag(itemId, removeGlobal ? null : tagName)
                                        else await axios.delete(`${api}/tracker/tracking/remove_entity`, { headers: { Authorization: `Bearer ${localStorage.getItem("authToken")}` }, data: { item_id: itemId } })
                                        setConfirmRemove(null)
                                    } catch (err) { } finally { setModalLoading(false) }
                                }}
                                itemName={confirmRemove.itemName}
                                tagName={confirmRemove.tagName}
                            />
                        )
                    }
                    <style>{`
          .filter-trigger{ background: transparent !important; background-color: transparent !important; border: 1px solid rgba(255,255,255,0.12) !important; color: #fff !important; border-radius: 12px; padding: 8px 12px; display:inline-flex; align-items:center; gap:6px; height:44px; cursor:pointer; font-weight:600; appearance: none; }
          .filter-trigger svg{ opacity:0.95 }
          .filter-trigger:hover, .filter-trigger:focus, .filter-trigger:active{ background: rgba(255,255,255,0.03) !important; color: #fff !important; border-color: rgba(255,255,255,0.18) !important; }
          .app_detail_txt h4{ max-width: 200px; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; display: block; margin: 0; }
          .app_detail_txt p{ max-width: 200px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; margin: 0 }
          .modal-app-img{ width: 96px; height: 54px; border-radius: 8px; object-fit: cover; display: block; }
          @media (max-width: 767px) {
            .modal-app-img{ width: 72px !important; height: 40px !important; border-radius: 6px !important; }
            .app_detail_txt h4, .app_detail_txt p{ max-width: 100% !important; }
          }
          @media (max-width: 600px) {
            .modal-app-img{ width: 60px !important; height: 34px !important; }
          }
          @media (max-width: 480px) {
            .modal-app-img{ width: 52px !important; height: 30px !important; }
          }
        `}</style>
                </Modal.Body >
            </Modal >
        </>
    )
}

export default KeywordSuggestionsDetailsModal
